// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import CategoryFilter from 'app/common/react/components/profile/ProfileSearch/CategoryFilter';
import ClearButton from 'app/common/react/components/profile/storefront/Filter/ClearButton';
import HeaderHeight from 'app/common/react/components/Header/Context/Context';
import i18n from 'app/common/lib/i18n';
import PriceFilter from 'app/common/react/components/profile/storefront/Filter/PriceFilter';
import SearchBox from 'app/common/react/components/profile/ProfileSearch/SearchBox';
import Selector from 'app/common/react/components/profile/storefront/Filter/Selector';
import SubmitButton from 'app/common/react/components/profile/storefront/Filter/SubmitButton';
import { HEADER_DEFAULT_HEIGHT } from 'app/common/react/components/Header/HeaderConstants';
import { type Dispatch } from 'redux';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import {
  StorefrontSearchSortTypeList,
  StorefrontSearchSortTypeDefaultValue,
} from 'app/common/react/components/profile/storefront/Filter/constants';
import {
  type Category,
  type Filter,
} from 'app/common/react/components/profile/ProfileSearch/types';
import {
  type SearchData,
  CancelSearch,
  ClearSearchFilter,
  CloseSearchPanel,
  DeleteDisplayFilter,
  submitSearch,
} from 'app/common/redux/actions/storefrontSearch';
import { Box, Divider, IconButton } from 'gestalt';

// To fix flow check issue
type OwnProps = {|
  handleSearchSubmit: () => void,
|};

type ReduxStateProps = {|
  appliedCategory: Category | null,
  appliedPrice: ?{
    max: number,
    min: number,
  },
  appliedQueryText: string,
  appliedSortBy: string,
  displayFilter: Filter,
  searchBoxInputFocus: boolean,
|};

type ReduxDispatchProps = {|
  cancelSearch: () => void,
  clearSearchFilter: () => void,
  closeSearchPanel: () => void,
  deleteDisplayFilter: () => void,
  submitSearch: (SearchData) => void,
|};

type Props = {| ...OwnProps, ...ReduxStateProps, ...ReduxDispatchProps |};

type State = {|
  category: Category | null,
  maxPrice: number,
  minPrice: number,
  queryText: string,
  sortByValue: string,
|};

export const SEARCH_PANEL_HEADER_HEIGHT = 64;
const SEARCH_PANEL_WIDTH = 390;
const DEFAULT_PRICE_VALUE = -1;

class FullHeightSearchPanel extends PureComponent<Props, State> {
  state = {
    category: this.props.appliedCategory,
    maxPrice:
      this.props.appliedPrice && this.props.appliedPrice.max
        ? this.props.appliedPrice.max
        : DEFAULT_PRICE_VALUE,
    minPrice:
      this.props.appliedPrice && this.props.appliedPrice.min
        ? this.props.appliedPrice.min
        : DEFAULT_PRICE_VALUE,
    queryText: this.props.appliedQueryText || '',
    sortByValue: StorefrontSearchSortTypeList.includes(this.props.appliedSortBy)
      ? this.props.appliedSortBy
      : StorefrontSearchSortTypeDefaultValue,
  };

  searchPanelRef: { current: null | HTMLDivElement } = React.createRef();

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e: MouseEvent) => {
    const panel = this.searchPanelRef.current;
    const isOutsideOfSearchPanel = panel && e.target instanceof Node && !panel.contains(e.target);

    if (isOutsideOfSearchPanel) {
      this.props.closeSearchPanel();
    }
  };

  handleClearSearchFilter = () => {
    this.setState({
      category: null,
      maxPrice: DEFAULT_PRICE_VALUE,
      minPrice: DEFAULT_PRICE_VALUE,
    });
  };

  handleCategoryChange = (category: Category) => {
    this.setState({
      category: this.state.category && this.state.category.id === category.id ? null : category,
    });
  };

  handleSortByChange = (sortBy: string) => {
    this.setState({
      sortByValue: sortBy,
    });
  };

  handleSearchTextChange = (query: string) => {
    this.setState({ queryText: query });
  };

  handleMaxPriceChange = (value: ?number) => {
    if (value === null || value === Number.MIN_VALUE) {
      value = DEFAULT_PRICE_VALUE;
    }
    this.setState({ maxPrice: value });
  };

  handleMinPriceChange = (value: ?number) => {
    if (value === null || value === Number.MIN_VALUE) {
      value = DEFAULT_PRICE_VALUE;
    }
    this.setState({ minPrice: value });
  };

  handleSubmitButton = () => this.handleSearchSubmit(this.state.queryText);

  handleSearchSubmit = (query: string) => {
    if (query === '' && this.state.sortByValue === StorefrontSearchSortTypeDefaultValue) {
      if (
        this.props.appliedQueryText !== query ||
        (this.state.minPrice === DEFAULT_PRICE_VALUE &&
          this.state.maxPrice === DEFAULT_PRICE_VALUE &&
          this.state.category === null)
      ) {
        // When there is no change in search text, sortBy, price filter or category filter,
        // we should use "All Product" feed instead of search feed
        this.props.cancelSearch();
        return;
      }
    }

    let filter = {};
    if (this.props.appliedQueryText !== query) {
      // Clear filters if search text has changed
      this.handleClearSearchFilter();
      this.props.deleteDisplayFilter();
    } else {
      if (
        this.state.minPrice !== DEFAULT_PRICE_VALUE ||
        this.state.maxPrice !== DEFAULT_PRICE_VALUE
      ) {
        filter = {
          price: {
            min: this.getMinPriceToBeSubmitted(),
            max: this.getMaxPriceToBeSubmitted(),
          },
        };
      }
      if (this.state.category) {
        filter = Object.assign({}, filter, {
          categories: [this.state.category],
        });
      }
    }
    this.props.handleSearchSubmit();
    this.props.submitSearch({
      filter,
      sortBy: this.state.sortByValue,
      queryText: query,
    });
  };

  getMinPriceToBeSubmitted = () => {
    const inputPrice = this.state.minPrice !== DEFAULT_PRICE_VALUE ? this.state.minPrice : 0;
    return this.props.displayFilter.price
      ? Math.max(inputPrice, this.props.displayFilter.price.min)
      : inputPrice;
  };

  getMaxPriceToBeSubmitted = () => {
    const inputPrice =
      this.state.maxPrice !== DEFAULT_PRICE_VALUE ? this.state.maxPrice : Number.MAX_VALUE;
    return this.props.displayFilter.price
      ? Math.min(inputPrice, this.props.displayFilter.price.max)
      : inputPrice;
  };

  renderSearchPanel() {
    return (
      <Box height="100%">
        {this.renderSearchPanelHeader()}
        <Box
          height={`calc(100% - ${SEARCH_PANEL_HEADER_HEIGHT}px)`}
          dangerouslySetInlineStyle={{
            __style: {
              overscrollBehavior: 'none',
              overflowY: 'auto',
              overflowX: 'hidden',
            },
          }}
        >
          {this.renderSearchPanelBody()}
        </Box>
      </Box>
    );
  }

  renderSearchPanelHeader() {
    return (
      <Box display="flex" justifyContent="end" paddingX={2} paddingY={2}>
        <IconButton
          onClick={this.props.closeSearchPanel}
          icon="cancel"
          iconColor="gray"
          size="sm"
          accessibilityLabel={i18n._('close search panel', 'accessibility label for close button')}
        />
      </Box>
    );
  }

  getPriceHasChanged = () => {
    const { appliedPrice } = this.props;
    if (appliedPrice) {
      return (
        this.getMinPriceToBeSubmitted() !== appliedPrice.min ||
        this.getMaxPriceToBeSubmitted() !== appliedPrice.max
      );
    } else {
      return (
        this.state.minPrice !== DEFAULT_PRICE_VALUE || this.state.maxPrice !== DEFAULT_PRICE_VALUE
      );
    }
  };

  renderSearchPanelBody() {
    const {
      appliedCategory,
      appliedQueryText,
      appliedSortBy,
      displayFilter,
      searchBoxInputFocus,
    } = this.props;
    const { maxPrice, minPrice, sortByValue, queryText } = this.state;
    const hasFilters = displayFilter && displayFilter.price;
    const categoryHasChanged = this.state.category !== appliedCategory;
    const enableSubmitButton =
      queryText !== appliedQueryText ||
      sortByValue !== appliedSortBy ||
      this.getPriceHasChanged() ||
      categoryHasChanged;
    return (
      <Box display="flex" direction="column">
        <Box paddingX={4} paddingY={3}>
          <SearchBox
            appliedQuery={appliedQueryText}
            inputFocused={searchBoxInputFocus}
            handleSearchSubmit={this.handleSearchSubmit}
            handleSearchTextChange={this.handleSearchTextChange}
          />
        </Box>
        {hasFilters && (
          <Box>
            <Box paddingX={4} paddingY={3}>
              <ClearButton handleClearButton={this.handleClearSearchFilter} />
            </Box>
            <Divider />
          </Box>
        )}
        {displayFilter &&
          displayFilter.categories && (
            <Box>
              <Box paddingX={4} paddingY={3}>
                <CategoryFilter
                  categories={displayFilter.categories}
                  currentCategory={this.state.category}
                  handleRadioButton={this.handleCategoryChange}
                />
              </Box>
              <Divider />
            </Box>
          )}
        {displayFilter &&
          displayFilter.price && (
            <Box>
              <Box paddingX={4} paddingY={3}>
                <PriceFilter
                  currencyCode={displayFilter.currency || 'USD'}
                  currentMaxValue={maxPrice}
                  currentMinValue={minPrice}
                  maxValue={displayFilter.price.max}
                  minValue={displayFilter.price.min}
                  handleMaxPrice={this.handleMaxPriceChange}
                  handleMinPrice={this.handleMinPriceChange}
                />
              </Box>
              <Divider />
            </Box>
          )}
        <Box paddingX={4} paddingY={3}>
          <Selector
            title={i18n._('Sort by', 'Header of the Selector')}
            selectionList={StorefrontSearchSortTypeList}
            currentSelection={this.state.sortByValue}
            handleSelectionChange={this.handleSortByChange}
          />
        </Box>
        <Divider />
        <Box
          position="absolute"
          bottom
          color="white"
          width="100%"
          paddingX={10}
          paddingY={8}
          dangerouslySetInlineStyle={{
            __style: {
              borderTop: '#dadada solid 1px',
            },
          }}
        >
          <SubmitButton
            handleSubmitButton={this.handleSubmitButton}
            disabled={!enableSubmitButton}
          />
        </Box>
      </Box>
    );
  }

  render() {
    const slideDirection = 'right';
    const searchPanelPosition = `-${SEARCH_PANEL_WIDTH}px`;
    const slideIn = true;

    return (
      <Box
        top
        position="fixed"
        height="100%"
        width={SEARCH_PANEL_WIDTH}
        dangerouslySetInlineStyle={{
          __style: {
            zIndex: 670,
            transition: `${slideDirection} .25s cubic-bezier(0.66, 0.025, 0.33, 0.975)`,
            [slideDirection]: searchPanelPosition,
            ...(slideIn
              ? {
                  [slideDirection]: '0',
                }
              : {}),
          },
        }}
      >
        <HeaderHeight.Consumer>
          {(headerHeight) => (
            <Box
              height={`calc(100vh - ${headerHeight}px)`}
              color="white"
              ref={this.searchPanelRef}
              tabIndex={-1}
              dangerouslySetInlineStyle={{
                __style: {
                  marginTop: headerHeight || HEADER_DEFAULT_HEIGHT,
                  borderLeft: null,
                  boxShadow: 'rgba(0, 0, 0, 0.1) -3px 4px 14px 0px',
                  overscrollBehavior: 'none',
                },
              }}
            >
              {this.renderSearchPanel()}
            </Box>
          )}
        </HeaderHeight.Consumer>
      </Box>
    );
  }
}

const mapStateToProps = ({ storefrontSearch }: ReduxState): ReduxStateProps => ({
  // By design only one category selection is allowed
  appliedCategory:
    storefrontSearch.currentSearch.filter.categories &&
    storefrontSearch.currentSearch.filter.categories.length
      ? storefrontSearch.currentSearch.filter.categories[0]
      : null,
  appliedPrice: storefrontSearch.currentSearch.filter.price,
  appliedQueryText: storefrontSearch.currentSearch.queryText,
  appliedSortBy: storefrontSearch.currentSearch.sortBy || StorefrontSearchSortTypeDefaultValue,
  displayFilter: storefrontSearch.displayFilter,
  searchBoxInputFocus: storefrontSearch.inputFocus,
});

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => ({
  cancelSearch: () => dispatch(CancelSearch),
  clearSearchFilter: () => dispatch(ClearSearchFilter),
  closeSearchPanel: () => dispatch(CloseSearchPanel),
  deleteDisplayFilter: () => dispatch(DeleteDisplayFilter),
  submitSearch: (params: SearchData) => dispatch(submitSearch(params)),
});

export default connect<Props, OwnProps, _, _, _, _>(
  mapStateToProps,
  mapDispatchToProps,
)(FullHeightSearchPanel);
